/**
 * DL-01 — MD5 in the browser: WebCrypto has none, so `spark-md5` (already the
 * intake's hasher, `apps/web … parse/hashes.ts`). Two uses:
 *
 *   Md5Stream   fed chunk by chunk WHILE a download streams to disk, so the
 *               bytes written are proved against the manifest's md5 without
 *               a second read;
 *   md5OfBlob   an existing file on disk, read in 4 MiB slices (the resume
 *               rule's second step — ~15 MB per image is fine).
 */
import SparkMD5 from "spark-md5";

function asArrayBuffer(chunk: Uint8Array): ArrayBuffer {
  return chunk.byteOffset === 0 &&
    chunk.byteLength === chunk.buffer.byteLength &&
    chunk.buffer instanceof ArrayBuffer
    ? chunk.buffer
    : chunk.slice().buffer;
}

const HEX_32 = /^[0-9a-f]{32}$/i;
const B64_MD5 = /^[A-Za-z0-9+/]{22}==$/;

/**
 * A served md5 as lower-case HEX, whichever form the server emits — hex
 * (`images.content_md5`) or base64 (the `Content-MD5` form, 24 chars) both
 * exist in the codebase. Anything else → null (= no usable md5, size-only).
 */
export function md5ToHex(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (HEX_32.test(s)) return s.toLowerCase();
  if (B64_MD5.test(s)) {
    const bin = atob(s);
    if (bin.length !== 16) return null;
    let hex = "";
    for (let i = 0; i < bin.length; i++)
      hex += bin.charCodeAt(i).toString(16).padStart(2, "0");
    return hex;
  }
  return null;
}

export class Md5Stream {
  private readonly spark = new SparkMD5.ArrayBuffer();

  append(chunk: Uint8Array): void {
    this.spark.append(asArrayBuffer(chunk));
  }

  /** The hex digest (lower case). */
  end(): string {
    return this.spark.end().toLowerCase();
  }
}

export const MD5_CHUNK_BYTES = 4 * 1024 * 1024;

/** `Blob.arrayBuffer()` where it exists; the FileReader form elsewhere (jsdom). */
export function blobBytes(blob: Blob): Promise<ArrayBuffer> {
  const direct = (blob as { arrayBuffer?: () => Promise<ArrayBuffer> })
    .arrayBuffer;
  if (typeof direct === "function") return direct.call(blob);
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      resolve(r.result as ArrayBuffer);
    };
    r.onerror = () => {
      reject(r.error ?? new Error("the file could not be read"));
    };
    r.readAsArrayBuffer(blob);
  });
}

export async function md5OfBlob(
  blob: Blob,
  chunkBytes = MD5_CHUNK_BYTES,
): Promise<string> {
  const h = new Md5Stream();
  for (let at = 0; at < blob.size; at += chunkBytes) {
    const slice = blob.slice(at, Math.min(blob.size, at + chunkBytes));
    h.append(new Uint8Array(await blobBytes(slice)));
  }
  return h.end();
}
